import { FormControl, InputLabel, makeStyles, MenuItem, Select } from "@material-ui/core";
import clsx from "clsx";
import { FC, useState } from "react";
import { useNotify, useRefresh } from "react-admin";

import { orderReservationDocRef } from "../../../utils/referencies";
import { applyUpdateTimestamp } from "../../../utils/timestamp";
import { OrderStatus } from "../types";
import EditableText from "./EditableText";
import useIsStore from "../../../hooks/useIsStore";

const useStyles = makeStyles((theme) => ({
  canceled: {
    color: theme.palette.error.main,
  },
  firstLetter: {
    "&:first-letter": {
      textTransform: "capitalize",
    },
  },
  formControl: {
    minWidth: 120,
    marginRight: "4px",
    "& label": {
      backgroundColor: "#fafafa",
    },
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
}));

const OrdersStatusText: FC<any> = ({ record: order }) => {
  const classes = useStyles({});
  const { isStore } = useIsStore();

  const [loading, setLoading] = useState(false);
  const [orderStatus, setOrderStatus] = useState<OrderStatus | undefined>(undefined);

  const notify = useNotify();
  const refresh = useRefresh();

  const isCanceledStatus = () => order.status === OrderStatus.canceled;
  const isDeliveredStatus = () => order.status === OrderStatus.delivered;
  const isFailedStatus = () => order.status === OrderStatus.failed;
  const isPendingStatus = () => order.status === OrderStatus.pending;
  const isDeliveringStatus = () => order.status === OrderStatus.delivering && orderStatus === OrderStatus.delivering;

  const handleUpdate = () => {
    setLoading(true);

    return new Promise<{ updatedValue: any }>(async (resolve, reject) => {
      try {
        await orderReservationDocRef(order.id).update(
          applyUpdateTimestamp({
            status: orderStatus,
            cancelationReason: null,
            canceledBy: null,
          })
        );
        resolve({ updatedValue: orderStatus });
        notify("Status has been updated", "success");
        setLoading(false);
        refresh();
      } catch (ex: any) {
        console.log(ex.message);
        reject({ originalValue: order.status });
        notify("Error on update status", "error");
        setLoading(false);
      }
    });
  };

  return (
    <EditableText
      sourceOriginalValue={order.status}
      canEdit={isStore && !isCanceledStatus() && !isDeliveredStatus() && !isFailedStatus() && !isPendingStatus()}
      onUpdate={handleUpdate}
      message={!orderStatus ? "Select status!" : isDeliveringStatus() ? "Same status, change it!" : "Save"}
      isDisabled={!orderStatus || isDeliveringStatus()}
      classesNames={clsx(
        {
          [classes.canceled]: order.status === OrderStatus.canceled,
        },
        classes.firstLetter
      )}
    >
      <FormControl size="small" variant="outlined" className={classes.formControl} disabled={loading}>
        <InputLabel id="edit-order-status-select">Status</InputLabel>
        <Select
          labelId="edit-order-status-select"
          onChange={(e) => {
            setOrderStatus(e.target.value as OrderStatus);
          }}
        >
          <MenuItem value={OrderStatus.delivering}>{OrderStatus.delivering}</MenuItem>
          <MenuItem value={OrderStatus.delivered}>{OrderStatus.delivered}</MenuItem>
        </Select>
      </FormControl>
    </EditableText>
  );
};

export default OrdersStatusText;
